import React, { useState, useEffect } from 'react';
import API, { UPLOADS_URL } from '../api/axios';
import SEO from '../components/SEO';
import PageBanner from '../components/PageBanner';

const CONTACT_FALLBACK = {
  phone1: '+91-9266333940',
  phone2: '+91-9266333941',
  email: 'info@sawssonipat.com',
  address: 'Block C, Ansal Sushant City, Sector 61, Kundli, Sonipat, Haryana 131028',
};

const Admission = () => {
  const [pageData, setPageData] = useState(null);
  const [contact, setContact] = useState(CONTACT_FALLBACK);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'Admission Enquiry',
    message: '',
    enquiry_type: 'Admission Enquiry'
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    API.get('/pages/public/admission').then(res => {
      if (res.data?.data) setPageData(res.data.data);
    }).catch(() => {});

    API.get('/contacts/public').then(res => {
      const c = res.data?.data;
      if (c?.phone) {
        const phones = String(c.phone).split(',').map(p => p.trim()).filter(Boolean);
        setContact({
          phone1: phones[0] ? (phones[0].startsWith('+') ? phones[0] : `+91-${phones[0]}`) : CONTACT_FALLBACK.phone1,
          phone2: phones[1] ? (phones[1].startsWith('+') ? phones[1] : `+91-${phones[1]}`) : '',
          email: c.email || CONTACT_FALLBACK.email,
          address: c.address || CONTACT_FALLBACK.address,
        });
      }
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        class_applying: form.subject !== 'Admission Enquiry' ? form.subject : (form.class_applying || ''),
      };
      await API.post('/enquiries/public', payload);
      setSubmitted(true);
      setForm({
        name: '',
        email: '',
        phone: '',
        subject: 'Admission Enquiry',
        message: '',
        enquiry_type: 'Admission Enquiry'
      });
    } catch (err) {
      alert('Failed to submit enquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const s = pageData?.sections || {};
  const banner = s.banner || {};
  const eligibility = s.eligibility || {
    kicker: 'Eligibility',
    heading: 'Who can apply.',
    note: "Age is calculated as on 31st March of the year of admission, while entry to Class I onwards is based on the student's previous class performance.",
    items: [
      { class: 'Nursery', desc: '3+ years as of 31st March' },
      { class: 'KG', desc: '4+ years as of 31st March' },
      { class: 'Class I onwards', desc: 'Based on previous class performance' }
    ]
  };
  const documents = s.documents || {
    kicker: 'Checklist',
    heading: 'Documents required.',
    intro: 'Keep these documents ready to make the registration process smooth and quick.',
    items: [
      { title: 'Birth Certificate', desc: 'For Nursery & KG applicants.', icon: 'fa-file-lines' },
      { title: 'Previous School Report Card', desc: 'Most recent report card from the school last attended.', icon: 'fa-file-invoice' },
      { title: 'Transfer Certificate', desc: 'If applicable, from the previous school.', icon: 'fa-folder-open' },
      { title: 'Passport Size Photographs', desc: "4 copies of the student's recent photographs.", icon: 'fa-image' },
      { title: 'Aadhaar Card Copy', desc: "A copy of the student's Aadhaar card.", icon: 'fa-id-card' }
    ]
  };
  const formSection = s.form_section || {
    kicker: 'Application',
    heading: 'Begin Your Application',
    intro: 'Fill out the form below and our admissions team will get back to you within 24 hours.'
  };

  const inputClass = "w-full border-b border-gray-300 bg-transparent px-0 py-3 text-[15px] text-gray-900 focus:border-[#a4862f] focus:outline-none focus:ring-0 transition-colors";
  const labelClass = "block text-[15px] font-bold uppercase tracking-widest text-[#1b3160]";

  const bannerImgSrc = pageData?.banner_image
    ? `${UPLOADS_URL}/${pageData.banner_image}`
    : '/images/page-banners/admission-guidliness.jpg';

  return (
    <>
      <SEO
        slug="admission"
        title={banner.title ? `${banner.title} | St. Andrews World School, Sonipat` : "Admission Process | St. Andrews World School, Sonipat"}
        description="Admissions are open at St. Andrews World School, Sonipat — Pre-Nursery to Grade XII. Check eligibility criteria and the documents required to apply."
        keywords="Admission Process, School Admission Sonipat, CBSE Admission Sonipat, St. Andrews World School"
      />

      {/* ─── HERO ─────────────────────────────────────────── */}
      <PageBanner
        title={banner.title || "Admission Process"}
        subtitle={banner.subtitle || "Admissions · Sonipat"}
        breadcrumb={banner.breadcrumb || "ADMISSION"}
        bgImage={bannerImgSrc}
      />

      {/* ─── ELIGIBILITY ─────────────────────────────────────────── */}
      <section id="eligibility" className="py-12 bg-white">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-12 lg:gap-16 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
            {/* Left Column: Content */}
            <div className="lg:sticky lg:top-32">
              <span className="text-[15px] font-bold uppercase tracking-[0.2em] text-[#1b3160]">
                {eligibility.kicker || 'Eligibility'}
              </span>
              <h2 className="mt-4 text-4xl leading-tight md:text-6xl text-[#1b3160]" style={{ fontFamily: '"Cormorant Garamond", "Playfair Display", serif' }}>
                {eligibility.heading || 'Who can apply.'}
              </h2>
              {eligibility.note && (
                <p className="mt-6 text-base leading-8 text-gray-600 whitespace-pre-line">
                  {eligibility.note}
                </p>
              )}
            </div>

            {/* Right Column: Table */}
            <div className="w-full">
              <div className="border border-gray-200 bg-white shadow-sm">
                <div className="grid grid-cols-[1fr_auto] items-center gap-4 border-b border-gray-200 bg-[#f9f8f4] px-6 py-5 sm:px-9">
                  <span className="text-[15px] font-bold uppercase tracking-[0.18em] text-[#1b3160]">Class of Entry</span>
                  <span className="text-[15px] font-bold uppercase tracking-[0.18em] text-[#1b3160]">Eligibility</span>
                </div>

                {(eligibility.items || []).map((item, idx) => (
                  <div key={idx} className="grid grid-cols-[1fr_auto] items-center gap-4 border-b border-gray-100 px-6 py-6 last:border-b-0 sm:px-9">
                    <span className="text-xl text-gray-900" style={{ fontFamily: '"Cormorant Garamond", "Playfair Display", serif' }}>{item.class}</span>
                    <span className="text-[#a4862f] md:text-base font-semibold">{item.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── DOCUMENTS ─────────────────────────────────────────── */}
      <section id="documents" className="bg-[#f9f8f4] py-12">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="border-b border-gray-200 pb-12">
            <span className="text-[15px] font-bold uppercase tracking-[0.2em] text-[#1b3160]">
              {documents.kicker || 'Checklist'}
            </span>
            <h2 className="mt-4 text-4xl leading-tight md:text-6xl text-[#1b3160]" style={{ fontFamily: '"Cormorant Garamond", "Playfair Display", serif' }}>
              {documents.heading || 'Documents required.'}
            </h2>
            {documents.intro && (
              <p className="mt-6 text-lg leading-relaxed text-gray-600 max-w-3xl">
                {documents.intro}
              </p>
            )}
          </div>

          <ul className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3 list-none p-0 m-0">
            {(documents.items || []).map((doc, idx) => (
              <li key={idx} className="group border border-gray-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                <div className="grid h-12 w-12 place-items-center border border-[#a4862f]/60 bg-white text-[#1b3160]">
                  <i className={`fas ${doc.icon || 'fa-file-lines'} text-xl`} />
                </div>
                <h3 className="mt-6 text-2xl leading-tight text-gray-900" style={{ fontFamily: '"Cormorant Garamond", "Playfair Display", serif' }}>
                  {doc.title}
                </h3>
                <p className="mt-3 text-[15px] leading-7 text-gray-500">
                  {doc.desc}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ─── ENQUIRY FORM ─────────────────────────────────────────── */}
      <section id="enquire" className="py-12 bg-white">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="border border-gray-200 shadow-sm max-w-[1000px] mx-auto overflow-hidden bg-white">
            <div className="bg-[#1b3160] px-8 py-10 md:px-12 md:py-14 text-white relative">
              <div className="absolute inset-0 bg-hero-grid opacity-20" aria-hidden="true" />
              <div className="relative z-10">
                <span className="text-[15px] font-bold uppercase tracking-[0.2em] text-[#d8a85f]">
                  {formSection.kicker || 'Application'}
                </span>
                <h2 className="mt-4 text-3xl text-white leading-tight md:text-5xl" style={{ fontFamily: '"Cormorant Garamond", "Playfair Display", serif' }}>
                  {formSection.heading || 'Begin Your Application'}
                </h2>
                <p className="mt-4 max-w-lg text-white/80 leading-relaxed text-base">
                  {formSection.intro || 'Fill out the form below and our admissions team will get back to you within 24 hours.'}
                </p>
              </div>
            </div>

            <div className="p-8 md:p-12">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[#1b3160]/10 text-[#1b3160]">
                    <i className="fas fa-check text-2xl" />
                  </div>
                  <h3 className="mb-3 text-2xl font-bold text-[#1b3160]" style={{ fontFamily: '"Cormorant Garamond", "Playfair Display", serif' }}>
                    Thank You!
                  </h3>
                  <p className="mb-8 text-gray-600">
                    We have received your admission enquiry. Our team will contact you shortly.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="inline-flex items-center justify-center bg-[#a4862f] text-white hover:bg-[#a4862f]/90 px-8 py-3 text-[15px] font-bold uppercase tracking-wider transition-colors cursor-pointer"
                  >
                    Submit Another
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.8fr] gap-12 lg:gap-16">
                  <div className="space-y-8">
                    <div>
                      <div className="text-[15px] font-bold uppercase tracking-widest text-[#a4862f] mb-2">Call Us</div>
                      <a href={`tel:${contact.phone1.replace(/[^0-9+]/g, '')}`} className="block text-gray-800 text-base font-medium hover:text-[#a4862f]">{contact.phone1}</a>
                      {contact.phone2 && <a href={`tel:${contact.phone2.replace(/[^0-9+]/g, '')}`} className="block text-gray-800 text-base font-medium hover:text-[#a4862f]">{contact.phone2}</a>}
                    </div>
                    <div>
                      <div className="text-[15px] font-bold uppercase tracking-widest text-[#a4862f] mb-2">Email Us</div>
                      <a href={`mailto:${contact.email}`} className="block text-gray-800 text-base font-medium hover:text-[#a4862f]">{contact.email}</a>
                    </div>
                    <div>
                      <div className="text-[15px] font-bold uppercase tracking-widest text-[#a4862f] mb-2">Visit Us</div>
                      <div className="text-gray-800 text-base font-medium leading-relaxed">{contact.address}</div>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <label className={labelClass}>Your Name <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          placeholder="John Doe"
                          value={form.name}
                          onChange={e => setForm({...form, name: e.target.value})}
                          required
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Email Address <span className="text-red-500">*</span></label>
                        <input
                          type="email"
                          placeholder="john@example.com"
                          value={form.email}
                          onChange={e => setForm({...form, email: e.target.value})}
                          required
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Phone Number</label>
                        <input
                          type="tel"
                          placeholder="+91"
                          value={form.phone}
                          onChange={e => setForm({...form, phone: e.target.value})}
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Class Applying For <span className="text-red-500">*</span></label>
                        <select
                          value={form.subject === 'Admission Enquiry' ? '' : form.subject}
                          onChange={e => setForm({...form, subject: e.target.value})}
                          className={inputClass}
                          required
                        >
                          <option value="" disabled>Select (Pre-Nursery to Grade XII)</option>
                          <option value="Pre-Nursery">Pre-Nursery</option>
                          <option value="Nursery">Nursery</option>
                          <option value="LKG">LKG</option>
                          <option value="UKG">UKG</option>
                          <option value="Grade I">Grade I</option>
                          <option value="Grade II">Grade II</option>
                          <option value="Grade III">Grade III</option>
                          <option value="Grade IV">Grade IV</option>
                          <option value="Grade V">Grade V</option>
                          <option value="Grade VI">Grade VI</option>
                          <option value="Grade VII">Grade VII</option>
                          <option value="Grade VIII">Grade VIII</option>
                          <option value="Grade IX">Grade IX</option>
                          <option value="Grade X">Grade X</option>
                          <option value="Grade XI">Grade XI</option>
                          <option value="Grade XII">Grade XII</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>Your Message <span className="text-red-500">*</span></label>
                      <textarea
                        placeholder="Tell us more about your child or any specific questions you have..."
                        value={form.message}
                        onChange={e => setForm({...form, message: e.target.value})}
                        rows={3}
                        required
                        className={`${inputClass} resize-y min-h-[80px]`}
                      />
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 pt-4">
                      <p className="text-[15px] text-gray-500 leading-relaxed max-w-sm">
                        Your details are safe with us. Our admissions counselor will contact you.
                      </p>
                      <button
                        type="submit"
                        disabled={loading}
                        className="inline-flex items-center justify-center bg-[#a4862f] text-white hover:bg-[#a4862f]/90 px-8 py-3.5 text-[15px] font-bold uppercase tracking-wider transition-colors disabled:opacity-70 w-full sm:w-auto cursor-pointer"
                      >
                        {loading ? 'Submitting...' : 'Submit Enquiry'} <i className="fas fa-arrow-right ml-2" aria-hidden="true" />
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Admission;
